// src/pages/AdminRoutesPage.tsx
//
// Lista de routes do DB (substitui config/routing.yaml).
// Reescrita usando o design system RAIOS (PageHeader, Card, Badge, Skeleton, EmptyState).

import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import type { RouteRow } from '@/lib/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { Network } from 'lucide-react';

/* ── Helpers ───────────────────────────────────────────────────────────────── */

const SIZING_BADGE: Record<RouteRow['sizing'], 'default' | 'info' | 'brand' | 'warning'> = {
  MICRO: 'default',
  CURTA: 'info',
  MEDIA: 'brand',
  LONGA: 'warning',
};

const MODE_BADGE: Record<RouteRow['mode'], 'default' | 'info' | 'brand'> = {
  single: 'default',
  chain: 'info',
  dag: 'brand',
};

/* ── Skeleton ──────────────────────────────────────────────────────────────── */

function TableSkeleton() {
  return (
    <Card className="overflow-hidden">
      <div className="p-4 space-y-3">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="flex gap-4">
            <Skeleton className="h-4 w-1/6" />
            <Skeleton className="h-4 w-1/5" />
            <Skeleton className="h-4 w-1/5" />
            <Skeleton className="h-4 w-1/12" />
            <Skeleton className="h-4 w-1/12" />
            <Skeleton className="h-4 w-12" />
          </div>
        ))}
      </div>
    </Card>
  );
}

/* ── Page ──────────────────────────────────────────────────────────────────── */

export default function AdminRoutesPage() {
  const [routes, setRoutes] = useState<RouteRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .listRoutes()
      .then(setRoutes)
      .catch((err: Error) => {
        setError(err.message ?? 'Falha ao carregar routes');
        setRoutes([]);
      })
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="p-4 md:p-6 space-y-6">
      <PageHeader
        title="Routes"
        description={`Mapeamento event_type → handler. Substitui config/routing.yaml legado. ${routes.length} rota${routes.length === 1 ? '' : 's'} registrada${routes.length === 1 ? '' : 's'}.`}
      />

      {error && (
        <Card className="bg-raios-danger/10 px-4 py-3">
          <p className="text-raios-danger-text text-sm">{error}</p>
        </Card>
      )}

      {loading ? (
        <TableSkeleton />
      ) : routes.length === 0 ? (
        <EmptyState
          icon={Network}
          title="Nenhuma rota registrada"
          description="O DB ainda não possui rotas configuradas."
        />
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-raios-elevated/40 text-left">
                <tr className="border-b border-raios-border">
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-raios-muted">Event Type</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-raios-muted">Handler</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-raios-muted">Target Slug</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-raios-muted">Sizing</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-raios-muted">Mode</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-raios-muted">Runner</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-raios-muted">Ativa</th>
                </tr>
              </thead>
              <tbody>
                {routes.map((r) => (
                  <tr
                    key={r.id}
                    className="border-b border-raios-border/60 hover:bg-raios-elevated/20 transition-colors"
                  >
                    <td className="px-4 py-3 font-mono text-xs text-raios-text">{r.event_type}</td>
                    <td className="px-4 py-3 font-mono text-xs text-raios-text">{r.handler_name}</td>
                    <td className="px-4 py-3 font-mono text-xs text-raios-text-secondary">
                      {r.target_slug ?? <span className="text-raios-muted">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant={SIZING_BADGE[r.sizing]}>{r.sizing}</Badge>
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant={MODE_BADGE[r.mode]}>{r.mode}</Badge>
                    </td>
                    <td className="px-4 py-3 text-xs text-raios-text-secondary">
                      {r.runner ?? <span className="text-raios-muted">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant={r.is_active ? 'success' : 'default'}>
                        {r.is_active ? 'sim' : 'não'}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
